/**
 * Feature: Pre-Fed Seed Catalogs & SOP Templates
 * /features/seed-catalogs-templates
 *
 * SoftwareApplication + FAQ schema.
 * Highlights plug-and-play templates and SOPs.
 */

import {
  FeatureFaq,
  FeatureSignup,
  FeatureHeroBadge,
} from '@/components/marketing/FeaturePageSections';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { Breadcrumbs } from '@/components/seo/Breadcrumbs';
import { getAuthLink } from '@/lib/links';
import { softwareApplicationSchema, faqPageSchema } from '@/lib/schemas';
import { Helmet } from 'react-helmet-async';
import {
  BookOpen,
  FileText,
  ClipboardList,
  PenTool,
  FolderOpen,
  ArrowRight,
  CheckCircle2,
  Layers,
  Sparkles,
  Settings,
} from 'lucide-react';

const faqs = [
  {
    question: 'What kind of templates are included?',
    answer:
      'Rooflyzer ships with high-converting sales scripts, proposal templates, contract templates, email/SMS drip sequences, SOP checklists for production and safety, line-item catalogs for shingles / metal / flat roofing, and marketing collateral templates. All editable from your dashboard.',
  },
  {
    question: 'Can I customize the templates?',
    answer:
      'Yes. Every template is fully editable. Modify text, add your branding, rearrange sections, or duplicate and create variants. Your customizations are saved per-company, so each branch or team can have their own versions.',
  },
  {
    question: 'What is a "Seed Catalog"?',
    answer:
      'A Seed Catalog is our pre-loaded database of materials, labor rates, and line items for common roofing jobs. Instead of manually entering every shingle brand, waste factor, and labor rate, you start with a comprehensive catalog and adjust prices to your market.',
  },
  {
    question: 'Are the SOPs based on real roofing operations?',
    answer:
      'Yes. Our SOPs are built from real-world roofing operations, including OSHA safety checklists, quality inspection protocols, warranty completion procedures, and customer handoff steps. They are designed by roofing operations experts, not generic software templates.',
  },
];

const templateCategories = [
  {
    icon: FileText,
    title: 'Sales Scripts & Proposals',
    items: [
      'Door knock scripts for storm & retail',
      'Phone follow-up scripts',
      'Insurance supplement scripts',
      'Multi-tier proposal templates (Economy/Standard/Premium)',
      'Financing offer scripts',
    ],
    color: 'purple',
  },
  {
    icon: ClipboardList,
    title: 'Operations SOPs',
    items: [
      'Pre-job safety inspection checklist',
      'Material ordering workflow',
      'Production crew daily checklist',
      'Quality control walkthrough',
      'Final customer walkthrough protocol',
    ],
    color: 'emerald',
  },
  {
    icon: PenTool,
    title: 'Contract & Legal Templates',
    items: [
      'Residential roofing contract',
      'Commercial roofing contract',
      'Change order template',
      'Warranty documentation',
      'Lien waiver templates',
    ],
    color: 'amber',
  },
  {
    icon: Layers,
    title: 'Line-Item Seed Catalogs',
    items: [
      'Asphalt shingle catalog (50+ SKUs)',
      'Metal roofing catalog',
      'Flat / TPO / EPDM catalog',
      'Labor rate templates by market',
      'Waste factor and complexity multipliers',
    ],
    color: 'blue',
  },
  {
    icon: Sparkles,
    title: 'Marketing Templates',
    items: [
      'Email drip sequence templates',
      'SMS follow-up sequences',
      'Review request templates',
      'Referral program templates',
      'Customer anniversary check-in',
    ],
    color: 'pink',
  },
  {
    icon: Settings,
    title: 'Admin & HR Templates',
    items: [
      'Employee onboarding checklist',
      'Commission structure templates',
      'Territory assignment forms',
      'Vehicle inspection logs',
      'Training completion tracking',
    ],
    color: 'cyan',
  },
];

export const SeedCatalogsPage = () => {
  const appSchema = softwareApplicationSchema({
    name: 'Rooflyzer OS — Seed Catalogs & SOP Templates',
    description:
      "Don't build from scratch. Start with pre-loaded sales scripts, SOPs, contract templates, and material catalogs. Fully editable modules accessible from your dashboard.",
    featureList: [
      'Pre-loaded line-item catalogs',
      'Sales script templates',
      'SOP checklists',
      'Contract templates',
      'Email/SMS sequence templates',
      'Marketing collateral templates',
    ],
    url: 'https://rooflyzer.pro/features/seed-catalogs-templates',
    offers: { price: '0', description: 'All templates included in every plan' },
  });

  const faqSchema = faqPageSchema(faqs);

  return (
    <>
      <SEOHead
        title="Roofing Product Catalogs & Material Templates"
        description="Pre-built material templates and product catalogs for roofing estimates. Save time on every proposal with reusable catalog items."
        canonicalUrl="/features/seed-catalogs-templates"
        keywords="roofing product catalog, roofing material templates, roofing estimates templates, roofing CRM templates"
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(appSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="pt-24 min-h-screen px-4 sm:px-6 pb-20">
        <div className="max-w-6xl mx-auto">
          <Breadcrumbs
            items={[
              { name: 'Features', url: '/features' },
              { name: 'Seed Catalogs & Templates', url: '/features/seed-catalogs-templates' },
            ]}
            className="mb-8"
          />

          {/* Hero */}
          <section className="text-center max-w-4xl mx-auto mb-20">
            <FeatureHeroBadge icon={BookOpen}>Ready on Day One</FeatureHeroBadge>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 tracking-tight">
              Don't Build From Scratch.{' '}
              <span className="text-white underline decoration-slate-600 decoration-2 underline-offset-8">
                Start With a Winning Blueprint.
              </span>
            </h1>
            <p className="text-base sm:text-lg md:text-xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Rooflyzer comes pre-loaded with high-converting sales scripts, standard operating
              procedures, and documentation templates. These aren't just PDFs; they are fully
              editable modules accessible directly from your dashboard, ensuring your team follows a
              proven winning blueprint from day one.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-[#050507] hover:bg-white/90 font-semibold px-8"
                onClick={() => window.location.assign(getAuthLink('/register'))}
              >
                Get All Templates Free
                <ArrowRight className="ml-2 size-5" />
              </Button>
            </div>
          </section>

          {/* Product Screenshot */}
          <section className="mb-24">
            <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />
              <img
                src="/product-frames/estimates.webp"
                alt="Rooflyzer material and product catalogs"
                className="w-full h-auto"
                loading="lazy"
              />
              <div className="absolute bottom-6 left-6 z-20">
                <span className="px-3 py-1 bg-white/10 backdrop-blur-md rounded-full text-sm text-white/90 border border-white/20">
                  Live product screenshot
                </span>
              </div>
            </div>
          </section>

          {/* Key Value Prop */}
          <section className="mb-24">
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { value: '200+', label: 'Line Items Pre-Loaded', icon: FolderOpen },
                { value: '12+', label: 'Proposal Templates', icon: FileText },
                { value: '30+', label: 'SOP Checklists', icon: ClipboardList },
                { value: '100%', label: 'Fully Editable', icon: PenTool },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="bg-white/5 rounded-2xl p-6 border border-white/10 text-center"
                >
                  <stat.icon className="size-6 text-emerald-400 mx-auto mb-3" />
                  <div className="text-3xl font-bold text-white font-mono mb-1">{stat.value}</div>
                  <div className="text-white/70 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Template Categories */}
          <section className="mb-24">
            <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-4">
              What's Included
            </h2>
            <p className="text-white/70 text-center mb-12 max-w-2xl mx-auto">
              Every category ships pre-loaded. Customize to fit your market, pricing, and brand.
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {templateCategories.map((category, i) => {
                const isEmerald = category.color === 'emerald';
                const bgClass = isEmerald ? 'bg-emerald-500/10' : 'bg-slate-800/80';
                const textClass = isEmerald ? 'text-emerald-400' : 'text-slate-300';
                const borderClass = isEmerald ? 'border-emerald-500/20' : 'border-slate-700';
                return (
                  <div
                    key={i}
                    className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-slate-600 transition-colors"
                  >
                    <div
                      className={`w-12 h-12 rounded-xl border ${bgClass} ${borderClass} flex items-center justify-center mb-4`}
                    >
                      <category.icon className={`size-6 ${textClass}`} />
                    </div>
                    <h3 className="text-lg font-bold text-white mb-4">{category.title}</h3>
                    <ul className="space-y-2">
                      {category.items.map((item, j) => (
                        <li key={j} className="flex items-start gap-2 text-white/70 text-sm">
                          <CheckCircle2 className={`size-4 ${textClass} mt-0.5 flex-shrink-0`} />
                          <span>{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </section>

          {/* How Catalogs Work */}
          <section className="mb-24 bg-white/5 backdrop-blur-xl rounded-3xl p-8 sm:p-12 border border-white/10">
            <h2 className="text-2xl font-bold text-white mb-8 text-center">
              How Seed Catalogs Save You Time
            </h2>
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  step: '1',
                  title: 'Start Pre-Loaded',
                  desc: 'Your account comes with materials, labor rates, and waste factors for common roofing jobs. No manual data entry.',
                },
                {
                  step: '2',
                  title: 'Customize Your Pricing',
                  desc: 'Adjust prices to your market. Set per-branch overrides. Add custom line items specific to your operation.',
                },
                {
                  step: '3',
                  title: 'Quote in Seconds',
                  desc: 'When you generate an estimate, your catalog auto-populates materials and labor. One click, full quote.',
                },
              ].map((item, i) => (
                <div key={i} className="text-center">
                  <div className="w-12 h-12 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center mx-auto mb-4 text-white font-mono font-bold text-xl">
                    {item.step}
                  </div>
                  <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                  <p className="text-white/70 text-sm leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* FAQ */}
          <FeatureFaq faqs={faqs} />

          {/* CTA */}
          <FeatureSignup
            title="Your Team Deserves a Winning Playbook"
            buttonLabel="Get All Templates — Free"
          >
            Stop reinventing the wheel. Start every rep, crew, and project with proven templates.
          </FeatureSignup>
        </div>
      </div>
    </>
  );
};

export default SeedCatalogsPage;
